"use client";
import { useEffect, useState } from "react";
import {
  getReferralCode,
  getReferralStats,
  ReferralStats,
} from "api/referralApi";
import { Navbar } from "home/Navbar";
import { Footer } from "home/Footer";
import { useAppSelector } from "lib/redux/hooks";
import { selectIsAuthenticationOpen } from "lib/redux/uiSlice";
import { AuthenticationOptions } from "components/AuthenticationOptions";
import { useDispatch } from "react-redux";
import { setIsAuthenticationOpen } from "lib/redux/uiSlice";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ShareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ChartBarIcon,
  UsersIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { toast, ToastContainer } from "react-toastify";

// const COLORS = [
//   "#1C75BC",
//   "#2563EB",
//   "#0E7490",
//   "#0891B2",
//   "#0F766E",
//   "#15803D",
//   "#7C3AED",
//   "#C026D3",
// ];

// Bar chart colors for 7 days
const BAR_COLORS = [
  "#1E40AF", // Deep Blue
  "#1C75BC", // Primary Blue
  "#2563EB", // Bright Blue
  "#0891B2", // Cyan Blue
  "#0E7490", // Teal Blue
  "#1D4ED8", // Royal Blue
  "#2563EB", // Sky Blue
];

export default function ReferralPage() {
  const [referralCode, setReferralCode] = useState<string>("");
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);
  const dispatch = useDispatch();

  useEffect(() => {
    // Check if user is authenticated
    if (typeof window === "undefined") return;
    const accessToken = localStorage.getItem("access-token");

    if (!accessToken) {
      // dispatch(setIsAuthenticationOpen(true));
      setIsLoading(false);
      return;
    }

    const fetchReferralData = async () => {
      try {
        setIsLoading(true);
        const [code, statsData] = await Promise.all([
          getReferralCode(),
          getReferralStats(),
        ]);
        setReferralCode(code);
        setStats(statsData);
      } catch (error) {
        console.error("Error fetching referral data:", error);
        toast.error("Failed to load referral data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralData();
  }, [dispatch]);

  const copyToClipboard = async () => {
    try {
      const referralUrl = `${window.location.origin}/resume-builder?utm_campaign=${referralCode}`;
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to copy referral link");
    }
  };

  const shareReferralCode = async () => {
    const referralUrl = `${window.location.origin}/resume-builder?utm_campaign=${referralCode}`;
    const shareText = `Join ResumeMate using my referral code and create professional resumes for free! ${referralUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "ResumeMate Referral",
          text: shareText,
          url: referralUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      copyToClipboard();
    }
  };

  const progressPercentage = stats ? (stats.totalUsers / 100) * 100 : 0;

  // Get progress bar color based on achievement
  const getProgressColor = (percentage: number) => {
    if (percentage >= 75) return "from-green-500 via-emerald-500 to-green-600";
    if (percentage >= 50) return "from-blue-500 via-cyan-500 to-blue-600";
    if (percentage >= 25) return "from-yellow-500 via-amber-500 to-orange-500";
    return "from-red-500 via-pink-500 to-red-600";
  };

  const getProgressBadgeColor = (percentage: number) => {
    if (percentage >= 75) return "bg-green-100 text-green-700";
    if (percentage >= 50) return "bg-blue-100 text-blue-700";
    if (percentage >= 25) return "bg-yellow-100 text-yellow-700";
    return "bg-orange-100 text-orange-700";
  };

  // Prepare data for country pie chart - show top 8 countries
  const topCountries =
    stats?.usersByCountry.filter((c) => c.users > 0).slice(0, 8) || [];
  // const pieChartData = topCountries.map((item) => ({
  //   ...item,
  //   name: item.country,
  //   value: item.users,
  // }));

  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="pt-2">
        <Navbar />
      </div>

      <main className="flex-grow py-6 md:pb-8 md:pt-2">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-8 text-center">
            <h1 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
              Referral Program
            </h1>
            <p className="text-lg text-gray-600 md:text-xl">
              Share ResumeMate with friends and earn rewards!
            </p>
          </div>

          {!referralCode && !stats ? (
            <div className="flex min-h-[500px] items-center justify-center">
              {isLoading ? (
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-gray-300 border-t-[#1C75BC]"></div>
              ) : (
                <div className="mx-auto max-w-md">
                  <div className="rounded-2xl border-2 border-gray-100 bg-white p-8 shadow-xl">
                    <div className="text-center">
                      <div className="mb-6 flex justify-center">
                        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
                          <span className="text-5xl">🪙</span>
                        </div>
                      </div>
                      <h2 className="mb-3 text-2xl font-bold text-gray-900">
                        Start Earning Coins Today!
                      </h2>
                      <p className="mb-6 text-gray-600">
                        Sign in to get your referral link and start earning
                        ResumeMate Coins for free premium subscription
                      </p>
                      <button
                        onClick={() => dispatch(setIsAuthenticationOpen(true))}
                        className="flex w-full items-center justify-center gap-3 rounded-lg bg-[#4285F4] px-6 py-4 font-semibold text-white shadow-lg transition-all hover:bg-[#3367D6] hover:shadow-xl"
                      >
                        <svg className="h-6 w-6" viewBox="0 0 24 24">
                          <path
                            fill="#ffffff"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#ffffff"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#ffffff"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#ffffff"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        Sign in with Google
                      </button>
                      <p className="mt-4 text-xs text-gray-500">
                        Quick, secure, and takes just a few seconds
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Coins Concept Hero Section */}
              <div className="rounded-3xl border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 p-8 shadow-xl">
                <div className="text-center">
                  <div className="mb-2 flex justify-center">
                    <div className="relative">
                      <div className="flex h-20 w-20 animate-bounce items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
                        <span className="text-4xl">💰</span>
                      </div>
                      <div className="absolute -right-2 -top-2 flex h-8 w-8 animate-pulse items-center justify-center rounded-full bg-blue-500 shadow-lg">
                        <span className="text-lg">✨</span>
                      </div>
                    </div>
                  </div>
                  <h2 className="mb-3 text-3xl font-bold text-gray-900 md:text-3xl">
                    Earn ResumeMate Coins!
                  </h2>
                  <p className="mb-6 text-lg text-gray-700 md:text-xl">
                    Share your referral link and earn coins for each person who
                    opens ResumeMate
                  </p>
                  <div className="mx-auto max-w-2xl">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div className="rounded-xl border border-yellow-200 bg-white/60 p-6 shadow-md backdrop-blur-sm">
                        <div className="mb-2 text-4xl font-bold text-[#1C75BC]">
                          1 new
                        </div>
                        <div className="text-sm text-gray-600">Visitor</div>
                      </div>
                      <div className="flex items-center justify-center">
                        <div className="text-3xl">→</div>
                      </div>
                      <div className="rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 p-6 shadow-lg">
                        <div className="mb-2 text-4xl font-bold text-white">
                          1 Coin
                        </div>
                        <div className="text-sm text-yellow-100">Earned</div>
                      </div>
                    </div>
                    <div className="mt-6 rounded-xl border-2 border-green-300 bg-green-100 p-4">
                      <p className="text-lg font-semibold text-green-800">
                        🎉 Collect 100 Coins = FREE Premium Subscription!
                      </p>
                      <p className="mt-1 text-sm text-green-700">
                        No sign-in or purchase required from referred users
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Referral Code and Coins Stats */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Referral Code Card */}
                <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-[#1C75BC] to-[#27AAE1] p-6 shadow-lg">
                  <div className="mb-4 flex items-center gap-3">
                    <ShareIcon className="h-8 w-8 text-white" />
                    <h2 className="text-2xl font-bold text-white">
                      Your Referral Code
                    </h2>
                  </div>
                  <div className="mb-4 rounded-lg bg-white/20 px-6 py-6 backdrop-blur-sm">
                    <p className="break-all text-center font-mono text-3xl font-bold tracking-wide text-white lg:text-4xl">
                      {`${window.location.origin}/resume-builder?utm_campaign=${referralCode}`}
                    </p>
                  </div>
                  <p className="mb-6 text-center text-sm text-white/90">
                    Anyone who opens ResumeMate with your link earns you a coin!
                    🪙
                  </p>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={copyToClipboard}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white px-6 py-3 font-semibold text-[#1C75BC] transition-all hover:bg-gray-100 hover:shadow-md"
                    >
                      {copied ? (
                        <>
                          <CheckCircleIcon className="h-5 w-5" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="h-5 w-5" />
                          Copy Link
                        </>
                      )}
                    </button>
                    <button
                      onClick={shareReferralCode}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-white/20 px-6 py-3 font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30"
                    >
                      <ShareIcon className="h-5 w-5" />
                      Share
                    </button>
                  </div>
                </div>

                {/* Coins Balance Card */}
                <div className="rounded-2xl bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-400 p-8 shadow-lg">
                  <div className="text-center">
                    <div className="mb-4 flex justify-center">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                        <span className="text-4xl">🪙</span>
                      </div>
                    </div>
                    <p className="mb-2 text-lg font-medium text-white/90">
                      Your Coin Balance
                    </p>
                    <p className="mb-2 text-6xl font-bold text-white">
                      {stats?.totalUsers || 0}
                    </p>
                    <p className="text-xl text-white/90">ResumeMate Coins</p>
                    <div className="mt-6 rounded-lg bg-white/20 p-4 backdrop-blur-sm">
                      <p className="text-sm text-white">
                        {progressPercentage >= 100 ? (
                          <span className="font-semibold">
                            🎉 You've earned your FREE premium subscription!
                          </span>
                        ) : (
                          <span>
                            Earn {100 - (stats?.totalUsers || 0)} more coins for
                            FREE premium!
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress to 100 Coins */}
              <div className="rounded-xl bg-white p-8 shadow-md">
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-gray-900">
                    Progress to Free Premium
                  </h3>
                  <div className="flex items-center gap-2">
                    <TrophyIcon className="h-6 w-6 text-yellow-500" />
                    <span
                      className={`rounded-full px-4 py-1 text-lg font-semibold ${getProgressBadgeColor(
                        progressPercentage
                      )}`}
                    >
                      {stats?.totalUsers || 0}/100 Coins
                    </span>
                  </div>
                </div>
                <div className="mb-4 h-8 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${getProgressColor(
                      progressPercentage
                    )} flex items-center justify-end pr-4 shadow-lg transition-all duration-500`}
                    style={{ width: `${progressPercentage}%` }}
                  >
                    {progressPercentage > 10 && (
                      <span className="text-sm font-bold text-white">
                        {progressPercentage.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Start</span>
                  <span className="font-semibold text-[#1C75BC]">
                    100 Coins = Free Premium! 🎁
                  </span>
                </div>
              </div>

              {/* Activity Chart */}
              <div className="rounded-xl bg-white p-6 shadow-md">
                <div className="mb-6 flex items-center gap-3">
                  <ChartBarIcon className="h-6 w-6 text-[#1C75BC]" />
                  <h3 className="text-xl font-bold text-gray-900">
                    Last 7 Days Coin Earnings
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats?.dailyUsersLast7Days || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                      stroke="#374151"
                      style={{ fontSize: "13px", fontWeight: 500 }}
                    />
                    <YAxis
                      stroke="#374151"
                      style={{ fontSize: "13px", fontWeight: 500 }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      labelFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                        });
                      }}
                      formatter={(value) => [`${value} Coins`, "Earned"]}
                    />
                    <Bar dataKey="users" radius={[8, 8, 0, 0]}>
                      {stats?.dailyUsersLast7Days.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={BAR_COLORS[index % BAR_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* How It Works Section */}
              <div className="rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-6 shadow-md md:p-8">
                <h3 className="mb-8 text-center text-3xl font-bold text-gray-900">
                  How to Earn Coins 💰
                </h3>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#1C75BC] to-[#27AAE1] shadow-lg">
                      <ShareIcon className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="mb-3 text-xl font-semibold text-gray-900">
                      Share Your Link
                    </h4>
                    <p className="text-gray-600">
                      Copy and share your unique referral link with friends,
                      family, or on social media
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg">
                      <UsersIcon className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="mb-3 text-xl font-semibold text-gray-900">
                      They Visit ResumeMate
                    </h4>
                    <p className="text-gray-600">
                      When someone opens ResumeMate using your link, you earn a
                      coin! No sign-up needed.
                    </p>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
                      <TrophyIcon className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="mb-3 text-xl font-semibold text-gray-900">
                      Get Free Premium
                    </h4>
                    <p className="text-gray-600">
                      Collect 100 coins and unlock your FREE premium
                      subscription with all pro features!
                    </p>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <div className="inline-block rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 font-semibold text-white shadow-lg">
                    ⚡ Start sharing now and watch your coins grow!
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      {isAuthenticationOpen && <AuthenticationOptions redirectTo="/referral" />}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
